import Typography from "@mui/material/Typography";
import {styled} from '@mui/material';
import React, {memo, useMemo} from "react";
import {C014StageCarouselPictureSettings} from "../../../common/constants/pictureSettings";
import {usePreviewContext} from "../../../context/PreviewContext";
import useWindowSize from "../../../utility/useWindowSize";
import {getAltText} from "../../fundamentals/CCOPicture";
import CCOResponsivePicture from "../../fundamentals/CCOResponsivePicture";
import PreviewElementBoxDrawer from "../../preview/PreviewElementBoxDrawer";
import {metaDataProperty} from "../../preview/previewMetaData";
import { CCOStageTextBox } from "../../fundamentals/CCOStageTextBox";
import {SosTeaserType} from "../../types";
import CCOAiGeneratedImageLabel, {useAiGeneratedImageLabel} from "../../fundamentals/CCOAiGeneratedImageLabel";

const HeroImage = styled("div")(({theme}) => ({
    position: "relative",
    height: '338px',
    [theme.breakpoints.up('sm')]: {height: '338px'},
    [theme.breakpoints.up('md')]: {height: '386px'},
    [theme.breakpoints.up('lg')]: {height: '514px'},
    [theme.breakpoints.up('xl')]: {height: '658px'},
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    overflow: "hidden",
    textAlign: "left"
}));

const ImageOverlay = styled("div")(() => ({
    position: "absolute",
    top: 0,
    bottom: 0,
    right: 0,
    left: 0
}));

const pictureStyleConst = {
    objectFit: "cover" as const,
    width: "100%",
    height: "100%",
    position: "absolute" as const
};

const baseImageStyleConst = {
    objectFit: "cover" as const,
    width: "100%",
    height: "100%",
};

interface TextContentProps {
    teaserTitle: string | undefined | null;
    isPreview: boolean;
    itemId: string;
    activateH1?: boolean;
}

const TextContent = memo(function TextContent({
                                                  teaserTitle,
                                                  isPreview,
                                                  itemId,
                                                  activateH1
                                              }: TextContentProps) {
    return (
        <Typography
            variant="landingPageTitle"
            component={activateH1 ? 'h1' : 'div'}
            sx={{
                '& span': {
                    color: 'inherit',
                    my: 0,
                    hyphens: 'none',
                    letterSpacing: '0.005em',
                    fontSize: {xs: '32px', sm: '40px', md: '48px', lg: '56px', xl: '80px'},
                    lineHeight: '90%'
                },
                m: 0,
                p: 0
            }}
        >
            <span {...metaDataProperty(isPreview, 'properties.teaserTitle', itemId)}>
                {teaserTitle}
            </span>
        </Typography>
    );
});

interface Props {
    item: SosTeaserType;
}

function C087LandingPageStageTeaser(props: Props) {
    const picture = props.item?.picture ?? undefined;
    const stageTeaserPositioning = picture?.mediaSettings?.positionForStageteaser;
    const isBlackBackground = props.item?.teaserSettings?.titleOnBlackBackground;
    const pictureAlt = getAltText(picture?.alt, picture?.title);
    const aiLabel = useAiGeneratedImageLabel(picture, pictureAlt);

    const {greaterThanWindowMedium, lowerThanWindowMedium} = useWindowSize();
    const isPreview = usePreviewContext().isPreview;

    const stageTeaserPositionStyle = useMemo(
        () =>
            stageTeaserPositioning
                ? {
                    height: "auto",
                    minHeight: "100%",
                    width: "100%",
                    position: "absolute",
                    top: stageTeaserPositioning === "top" ? "0" : undefined,
                    bottom: stageTeaserPositioning === "bottom" ? "0" : undefined
                }
                : {},
        [stageTeaserPositioning]
    );

    function renderTextContent() {
        return (
            <CCOStageTextBox blackBg={isBlackBackground}>
                <TextContent
                    teaserTitle={props.item.teaserTitle}
                    isPreview={isPreview}
                    itemId={props.item.id}
                    activateH1={!!props.item?.teaserSettings?.activateH1}
                />
            </CCOStageTextBox>
        );
    }

    return (
        <PreviewElementBoxDrawer propertyId={props.item.id}>
            <div data-cypress="landingPageStageTeaser">
                <HeroImage>
                    {picture && (
                        <>
                            <CCOResponsivePicture
                                dataCypress={"stageTeaserImage"}
                                pictureSetting={C014StageCarouselPictureSettings}
                                picture={picture}
                                hasPriority={!stageTeaserPositioning}
                                alt={aiLabel?.imageAlt ?? pictureAlt}
                                imageStyle={{...baseImageStyleConst, ...stageTeaserPositionStyle}}
                                pictureStyle={pictureStyleConst}
                            />
                            <ImageOverlay/>
                            {aiLabel && <CCOAiGeneratedImageLabel label={aiLabel} variant='tile'/>}
                        </>
                    )}
                    {greaterThanWindowMedium && renderTextContent()}
                </HeroImage>
                {lowerThanWindowMedium && renderTextContent()}
            </div>
        </PreviewElementBoxDrawer>
    );
}

export default C087LandingPageStageTeaser;
